
    import Component from "vue-class-component";
    import Vue from "vue";
    import {CertificateTypeLightModel, SysAdminCertificateTypesClient, ResultError} from "@/api-client";
    import { BvTableFieldArray } from "bootstrap-vue";
    import TableEmptySlot from "@/features/shared/components/tables/TableEmptySlot.vue";
    import HeadingWave from "@/features/shared/components/HeadingWave.vue";
    import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
    import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
    import {Prop} from "vue-property-decorator";
    import {NavigationGuardNext} from "vue-router/types/router";
    import toasts from "@/components/Toasts";
    import {translateTrueFalse} from "@/features/shared/TrueFalseTranslator";
    import {debounceFn} from "debounce-decorator-ts";

    interface Props {
        preloadedCertificateTypes: CertificateTypeLightModel[];
    }

    @Component({
        components: {
            TableEmptySlot,
            HeadingWave,
            SectionPageHeading,
            ButtonWithConfirm
        },
        beforeRouteEnter(to, from, next: NavigationGuardNext<CertificateTypes>) {
            const props = to.meta!.props as Props;
            Promise.all([
                new SysAdminCertificateTypesClient().list()
            ]).then(values => {
                props.preloadedCertificateTypes = values[0];
                next();
            }).catch(e => next(e))
        }
    })
    export default class CertificateTypes extends Vue implements Props {
        @Prop({ required: true })
        preloadedCertificateTypes!: CertificateTypeLightModel[];

        certificateTypes = this.preloadedCertificateTypes;
        filteredCertificateTypes = this.certificateTypes;

        searchPhrase = "";
        initialSort = { sortBy: "name", sortDesc: false };

        created() {
            this.getParametersFromRouteOrDefault();

            this.$watch(() => this.searchPhrase, () => {
                this.reflectParametersToRoute();
            });

            this.$watch(() => this.$route.query, () => {
                this.search();
            }, { deep: true, immediate: true });
        }

        search(){
            this.filteredCertificateTypes = this.certificateTypes.filter(o => o.name.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0 || o.id.toLowerCase().indexOf(this.searchPhrase.toLowerCase()) >= 0);
        }

        @debounceFn(100) // Prevents possible double-update
        reflectParametersToRoute(): void {
            this.$router.replace({
                query: {
                    // Setting to 'undefined' removes parameter from URL.
                    searchPhrase: this.searchPhrase || undefined
                }
            });
        }

        getParametersFromRouteOrDefault() {
            const route = this.$route as any;
            const query = route.query;
            this.searchPhrase = query.searchPhrase ? query.searchPhrase : '';
        }

        navigateToDetails(certificateType: CertificateTypeLightModel) {
            this.$router.push({
                name: "certificate-type",
                params: { id: certificateType.id },
            });
        }

        async deactivate(certificateType: CertificateTypeLightModel) {
            await new SysAdminCertificateTypesClient().deactivate(certificateType.id).then(
                success => {
                    certificateType.isActive = false;
                    toasts.success("Tunnistuse tüüp on deaktiveeritud");
                },
                (error: ResultError) => error.messagesFlattened.forEach(x => toasts.failure(x)));
        }

        async activate(certificateType: CertificateTypeLightModel) {
            await new SysAdminCertificateTypesClient().activate(certificateType.id).then(
                success => {
                    certificateType.isActive = true;
                    toasts.success("Tunnistuse tüüp on aktiveeritud");
                },
                (error: ResultError) => error.messagesFlattened.forEach(x => toasts.failure(x)));
        }

        fields: BvTableFieldArray = [
            { key: "name", label: this.$ls.Global_Label_Name, sortable: true, thClass: "w-50" },
            { key: "id", label: this.$ls.Global_Label_Id, sortable: false },
            { key: "isActive", label: this.$ls.Global_Label_Active, sortable: false, formatter: (value: any, key: any, item: CertificateTypeLightModel): string => translateTrueFalse(item.isActive) },
            { key: "actions", label: "", headerTitle: "actions", sortable: false, tdClass: "table-actions" },
            { key: "linkIcon", label: "", sortable: false, tdClass:"table-actions" }
        ];
    }
